import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Facebook } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { CATEGORIES, categoryById } from "@/data/categories";
import { useSettings } from "@/hooks/useSettings";
import { useSEO } from "@/hooks/useSEO";
import { API, telHref } from "@/lib/api";

export default function Apply() {
  const [params] = useSearchParams();
  const preselect = params.get("vehicle") || "";
  const settings = useSettings();
  const phone = settings.phone || "591 063 123";
  const email = settings.email || "taxiparkgeorgia@gmail.com";
  const address = settings.address || "თბილისი, პეტრე ქავთარაძის 48";
  const maps = settings.maps_url || "https://share.google/OWcepd2xfwMkeLMy1";
  const whatsapp = settings.whatsapp_url || "https://wa.me/995591063123";
  const facebook = settings.facebook || "";
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "", comment: "" });
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "განაცხადი — ტაქსის მანქანის დაჯავშნა | taxipark.ge",
    description: "დატოვე განაცხადი ტაქსის მანქანის ქირაობაზე: აირჩიე Toyota Prius, Tesla ან BYD და ჩვენი გუნდი დაგიკავშირდება ერთი სამუშაო დღის განმავლობაში.",
    path: "/apply",
  });

  useEffect(() => {
    if (!preselect) return;
    const cat = categoryById(preselect);
    if (cat) {
      setForm((f) => ({ ...f, vehicle: cat.id }));
      return;
    }
    axios.get(`${API}/vehicles`).then((r) => {
      const byId = r.data.find((v) => v.id === preselect);
      if (byId) setForm((f) => ({ ...f, vehicle: byId.category_id }));
    }).catch(() => {});
  }, [preselect]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("შეავსე სახელი და ტელეფონის ნომერი");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      toast.success("განაცხადი გაიგზავნა — მალე დაგიკავშირდებით!");
      setForm({ name: "", phone: "", vehicle: "", comment: "" });
    } catch {
      toast.error("გაგზავნა ვერ მოხერხდა — სცადე ხელახლა ან დაგვირეკე");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/40 transition-colors";

  return (
    <main data-testid="apply-page" className="pt-28 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">განაცხადი</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">დაიწყე მუშაობა დღესვე</h1>
          <p className="text-lg text-gray-400 mt-5 leading-relaxed">
            შეავსე ფორმა და ჩვენი გუნდი დაგიკავშირდება ერთი სამუშაო დღის განმავლობაში.
          </p>
          <div className="mt-10 space-y-5 text-gray-300">
            <a href={telHref(phone)} data-testid="apply-phone" className="flex items-center gap-3 hover:text-white transition-colors group">
              <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors"><Phone className="w-5 h-5 text-[#D4AF37]" /></span>
              {phone}
            </a>
            <a href={`mailto:${email}`} data-testid="apply-email" className="flex items-center gap-3 hover:text-white transition-colors">
              <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center"><Mail className="w-5 h-5 text-[#D4AF37]" /></span>
              {email}
            </a>
            <a href={maps} target="_blank" rel="noreferrer" data-testid="apply-address" className="flex items-center gap-3 hover:text-white transition-colors group">
              <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors"><MapPin className="w-5 h-5 text-[#D4AF37]" /></span>
              <span className="underline-offset-4 decoration-white/30 group-hover:underline group-hover:decoration-[#D4AF37]/60">{address}</span>
            </a>
            <a href={whatsapp} target="_blank" rel="noreferrer" data-testid="apply-whatsapp" className="flex items-center gap-3 hover:text-white transition-colors group">
              <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors"><WhatsAppIcon className="w-5 h-5 text-[#D4AF37]" /></span>
              <span className="underline-offset-4 decoration-white/30 group-hover:underline group-hover:decoration-[#D4AF37]/60">WhatsApp: {phone}</span>
            </a>
            {facebook && (
              <a href={facebook} target="_blank" rel="noreferrer" data-testid="apply-facebook" className="flex items-center gap-3 hover:text-white transition-colors group">
                <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors"><Facebook className="w-5 h-5 text-[#D4AF37]" /></span>
                <span className="underline-offset-4 decoration-white/30 group-hover:underline group-hover:decoration-[#D4AF37]/60">Facebook</span>
              </a>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 card-lux p-7 sm:p-10 space-y-6" data-testid="application-form">
          <div>
            <label htmlFor="apply-name" className="block text-sm font-medium text-gray-300 mb-2">სახელი</label>
            <input
              id="apply-name"
              data-testid="application-name-input"
              type="text"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="მაგ. გიორგი მაისურაძე"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="apply-phone" className="block text-sm font-medium text-gray-300 mb-2">
              ტელეფონის ნომერი <span className="text-[#D4AF37]">*</span>
            </label>
            <input
              id="apply-phone"
              data-testid="application-phone-input"
              type="tel"
              required
              value={form.phone}
              onChange={set("phone")}
              placeholder="+995 5XX XX XX XX"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="apply-vehicle" className="block text-sm font-medium text-gray-300 mb-2">სასურველი ავტომობილი</label>
            <select
              id="apply-vehicle"
              data-testid="application-vehicle-select"
              value={form.vehicle}
              onChange={set("vehicle")}
              className={inputCls}
            >
              <option value="" className="bg-[#141721]">ჯერ არ ვიცი — მინდა კონსულტაცია</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id} className="bg-[#141721]">
                  {c.name} {c.years}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="apply-comment" className="block text-sm font-medium text-gray-300 mb-2">კომენტარი</label>
            <textarea
              id="apply-comment"
              data-testid="application-comment-input"
              rows={4}
              value={form.comment}
              onChange={set("comment")}
              placeholder="დაწერე შენი კითხვა ან სურვილი..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            data-testid="application-form-submit"
            disabled={loading}
            className="btn-gold w-full text-base disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "იგზავნება..." : "განაცხადის გაგზავნა"} <Send className="w-5 h-5" />
          </button>
          <p className="text-xs text-gray-500 text-center">
            ფორმის გაგზავნით ეთანხმები{" "}
            <a href="/privacy" className="text-[#E5C378] hover:underline">კონფიდენციალურობის პოლიტიკას</a>.
          </p>
        </form>
      </div>
    </main>
  );
}
